import type { HelpdeskAuthorType, IHelpdeskAttachment, IHelpdeskMessage } from "@kuika/kuika-cl-model";
import { formatTicketDay } from "./helpdesk-tickets";
import type { IHelpdeskUploadProgress } from "./helpdesk-upload";

/** Messages from one author this close together read as one turn: one header, tight spacing. */
export const HELPDESK_TURN_WINDOW_MS: number = 5 * 60 * 1000;

export interface IHelpdeskThreadDay {
  kind: "day";
  /** The local calendar day; also the React key. */
  key: string;
  label: string;
}

export interface IHelpdeskThreadMessage {
  kind: "message";
  message: IHelpdeskMessage;
  /** Continues the previous message's turn: no avatar or author line of its own. */
  continued: boolean;
}

export type HelpdeskThreadItem = IHelpdeskThreadDay | IHelpdeskThreadMessage;

/** The customer's side (the requester and their colleagues) sits right; Kuika Support sits left. */
export function isCustomerSide(authorType: HelpdeskAuthorType): boolean {
  return authorType === "requester" || authorType === "colleague";
}

function localDayKey(time: number): string {
  const date: Date = new Date(time);
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

function continuesTurn(
  previous: IHelpdeskMessage,
  previousTime: number,
  message: IHelpdeskMessage,
  time: number
): boolean {
  const gap: number = time - previousTime;
  return (
    previous.authorType === message.authorType &&
    previous.authorName === message.authorName &&
    gap >= 0 &&
    gap <= HELPDESK_TURN_WINDOW_MS
  );
}

/**
 * The thread as drawn: a day line wherever the local calendar day changes, and consecutive messages
 * of one author grouped into turns. The order is the ITSM's (oldest first); a message without a
 * readable time gets no day line and starts its own turn.
 */
export function buildThread(messages: readonly IHelpdeskMessage[]): HelpdeskThreadItem[] {
  const items: HelpdeskThreadItem[] = [];
  let previous: IHelpdeskMessage | null = null;
  let previousTime: number = Number.NaN;
  let currentDay: string | null = null;

  for (const message of messages) {
    const time: number = Date.parse(message.createdAt);
    const hasTime: boolean = Number.isFinite(time);
    let startsDay: boolean = false;
    if (hasTime) {
      const day: string = localDayKey(time);
      if (day !== currentDay) {
        currentDay = day;
        startsDay = true;
        items.push({ kind: "day", key: day, label: formatTicketDay(message.createdAt) });
      }
    }

    const continued: boolean =
      previous !== null &&
      !startsDay &&
      hasTime &&
      Number.isFinite(previousTime) &&
      continuesTurn(previous, previousTime, message, time);
    items.push({ kind: "message", message, continued });

    previous = message;
    previousTime = time;
  }

  return items;
}

/** Up to two letters for a person's avatar: the first letters of their first and last names. */
export function authorInitials(name: string): string {
  const words: string[] = name
    .trim()
    .split(/\s+/)
    .filter((word: string): boolean => word.length > 0);
  if (words.length === 0) return "";
  // Array.from keeps a letter outside the BMP whole instead of half of a surrogate pair.
  const first: string = Array.from(words[0])[0] ?? "";
  const last: string = words.length > 1 ? (Array.from(words[words.length - 1])[0] ?? "") : "";
  return (first + last).toUpperCase();
}

/** A reply on its way to the ITSM, shown in the thread at once instead of after the re-read. */
export interface IHelpdeskPendingReply {
  body: string;
  files: File[];
  /** When Send was pressed, as ISO 8601: where the reply sits in the thread until the real one lands. */
  startedAt: string;
  /** The ITSM's id once it accepted the message; the copy stays until a re-read brings that message. */
  messageId: number | null;
  progress: IHelpdeskUploadProgress | null;
}

/** Stands in for the pending reply's id; ITSM message ids are positive. */
export const PENDING_MESSAGE_ID: number = -1;

/** Whether the thread already holds the real message, so the pending copy must not be drawn too. */
export function isReplyInThread(reply: IHelpdeskPendingReply, messages: readonly IHelpdeskMessage[]): boolean {
  return (
    reply.messageId !== null && messages.some((message: IHelpdeskMessage): boolean => message.id === reply.messageId)
  );
}

/** The pending reply as a message of the requester: only the requester can reply (`canReply`). */
export function pendingReplyMessage(reply: IHelpdeskPendingReply, requesterName: string): IHelpdeskMessage {
  return {
    id: PENDING_MESSAGE_ID,
    body: reply.body,
    authorName: requesterName,
    authorType: "requester",
    createdAt: reply.startedAt,
    attachments: reply.files.map((file: File, index: number): IHelpdeskAttachment => ({
      id: `pending-${index}`,
      fileName: file.name,
      sizeBytes: file.size,
      contentType: file.type,
      isImage: file.type.startsWith("image/"),
      status: "processing"
    }))
  };
}

/** What the pending reply is doing; empty once it is sent and only the re-read is outstanding. */
export function describePendingReply(reply: IHelpdeskPendingReply): string {
  if (reply.progress) {
    return `Uploading file ${reply.progress.current} of ${reply.progress.total} · ${reply.progress.percent}%`;
  }
  return reply.messageId === null ? "Sending…" : "";
}
